/**
 * Cliente HTTP del API.
 *
 * En el navegador no hay URL base: se pega a `/api` del mismo origen y quien
 * reenvía es Vite en desarrollo o nginx en producción. Así la web compilada es
 * idéntica en todas las instalaciones y no hay que recompilarla por condominio.
 * En el APK nadie reenvía, y `baseApi()` antepone el servidor que se eligió al
 * arrancar; en el navegador devuelve vacío y esto queda igual que antes.
 */

import { baseApi } from './servidor';

const CLAVE_TOKEN = 'kodica.token';

export class ErrorApi extends Error {
  constructor(
    readonly estado: number,
    mensaje: string,
    readonly errores?: Array<{ campo: string; mensaje: string }>,
  ) {
    super(mensaje);
    this.name = 'ErrorApi';
  }
}

export const almacenToken = {
  leer: (): string | null => localStorage.getItem(CLAVE_TOKEN),
  guardar: (token: string): void => localStorage.setItem(CLAVE_TOKEN, token),
  borrar: (): void => localStorage.removeItem(CLAVE_TOKEN),
};

interface Opciones {
  metodo?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  cuerpo?: unknown;
  /**
   * Que un 401 **no** significa que la sesión haya caducado.
   *
   * Por omisión un 401 borra el token, y para casi todo es lo correcto: el
   * servidor está diciendo que quien pregunta ya no es nadie. Pero hay
   * peticiones donde el 401 habla de un dato que viajó en el cuerpo y no de la
   * sesión —cambiar la contraseña, donde significa «la actual no es esa»— y
   * ahí borrarlo echa fuera a quien solo se equivocó tecleando.
   */
  noCierraSesion?: boolean;
}

export async function pedir<T>(ruta: string, opciones: Opciones = {}): Promise<T> {
  const token = almacenToken.leer();

  const respuesta = await fetch(`${baseApi()}/api${ruta}`, {
    method: opciones.metodo ?? 'GET',
    headers: {
      ...(opciones.cuerpo ? { 'Content-Type': 'application/json' } : {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: opciones.cuerpo ? JSON.stringify(opciones.cuerpo) : undefined,
  });

  return interpretar<T>(respuesta, opciones.noCierraSesion);
}

/**
 * Sube un archivo (multipart). Se usa `FormData` y **no** se fija
 * `Content-Type`: el navegador lo pone con el `boundary`, que a mano se
 * omitiría y el servidor no sabría dónde empieza cada parte. El biométrico
 * facial entra por aquí.
 */
export async function subirArchivo<T>(ruta: string, archivo: Blob, nombre = 'archivo'): Promise<T> {
  const token = almacenToken.leer();
  const forma = new FormData();
  forma.append('archivo', archivo, nombre);

  const respuesta = await fetch(`${baseApi()}/api${ruta}`, {
    method: 'POST',
    headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    body: forma,
  });

  return interpretar<T>(respuesta);
}

async function interpretar<T>(respuesta: Response, noCierraSesion = false): Promise<T> {
  if (respuesta.status === 204) return undefined as T;

  const datos = (await respuesta.json().catch(() => ({}))) as Record<string, unknown>;

  if (!respuesta.ok) {
    // El token caducó o al usuario lo dieron de baja a media jornada. Salvo
    // cuando quien llama avisa de que ese 401 habla de otra cosa: ver
    // `noCierraSesion`.
    if (respuesta.status === 401 && !noCierraSesion) almacenToken.borrar();

    throw new ErrorApi(
      respuesta.status,
      (datos['mensaje'] as string) ?? (datos['message'] as string) ?? 'Error de comunicación',
      datos['errores'] as Array<{ campo: string; mensaje: string }> | undefined,
    );
  }

  return datos as T;
}
